import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";
import LandInfo, { LandInfoHandle } from "./LandInfo/LandInfo";
import backArrow from "../../asset/images/arrow/arrow.svg";
import { useNavigate } from "react-router-dom";
import TractorInfo from "./TractorInfo/TractorInfo";
import CloseIcon from "../../asset/images/new/Close.svg";
import TaskInfo from "./TaskInfo/TaskInfo";
import { isMobile } from "../../component/function/function";
import { AnalysisForm, initialForm } from "./machine";
import TractorService from "../../service/tractor/tractorApi";
import {
  convertPriceUnit,
  convertQuantityUnit,
  convertUnitPriceToPerM2,
  fromM2,
  toM2,
} from "./function";
import MinAreaAlert from "./MinAreaAlert";
// import backArrow from "./../../../asset/images/arrow/arrow.svg";

// 생산량 기본값 = 303kg/10a
const QUANTITY_BASE = 303;
// 단가 기본값 = 4800 원/kg
const UNIT_PRICE_BASE = 4800;
// 영농 단가 기본값 = 75000 원/10a
const FARM_AGENT_UNIT = 75000;

const tabList = ["영농 정보", "트랙터/부속기", "작업 및 가중치"];

const defaultCost = {
  farmAgentUnitPrice: 75000,
  empLaborCostPerUnit: 119790,
  ownLaborCostPerUnit: 300000,
  materialCostPerUnit: 239580,
  waterHeatingCostPerUnit: 35000,
  farmEquipmentRentalCostPerUnit: 0,
  sgaPerUnit: 335842,
};

function MachineReport({
  location2,
  position,
  temp,
  setReportIdx5,
  setClose,
}: any) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("영농 정보");
  const [menuHeight, setMenuHeight] = useState<number>(0);
  const tabRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<AnalysisForm>(initialForm);
  const landInfoRef = useRef<LandInfoHandle | null>(null);
  const [showAlert, setShowAlert] = useState(false);

  const fixedRef = useRef<HTMLDivElement>(null);
  const [fixedH, setFixedH] = useState(48); // 기본 48px, 실제 DOM으로 보정

  const isDesktop = typeof window !== "undefined" && window.innerWidth >= 1024;
  const isFixed = isDesktop && position > menuHeight;

  const handleBack = () => {
    const idx = tabList.indexOf(activeTab);
    if (idx <= 0) {
      navigate(-1);
    } else {
      setActiveTab(tabList[idx - 1]);
    }
  };

  const handleClickTab = (t: string) => {
    if (activeTab === "영농 정보" && t !== "영농 정보") {
      const ok = landInfoRef.current?.validateAndScroll?.();
      if (!ok) return; // 필수값 미입력 → 탭 이동 막기
      if (formData.allArea < 1000) {
        setShowAlert(true);
        return;
      }
    }

    setActiveTab(t);
  };

  useEffect(() => {
    setMenuHeight(tabRef.current?.getBoundingClientRect().top || 0);
  }, []);
  useEffect(() => {
    if (fixedRef.current) {
      setFixedH(fixedRef.current.getBoundingClientRect().height || 48);
    }
  }, [isFixed]);

  useEffect(() => {
    if (isMobile(window.innerWidth)) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (!temp?.current) return;

    temp.current?.scrollTo({
      top: 559,
      behavior: "smooth",
    });
  }, [activeTab]);

  // 초기값
  useEffect(() => {
    const pnu = JSON.parse(sessionStorage.areaList)[0].info.pnuCode || "";
    const allArea = Number(
      JSON.parse(sessionStorage.areaList)[0].info.pyeong || 0
    );
    const quantity = convertQuantityUnit(QUANTITY_BASE, "10a", "평");
    const farmAgentUnitPrice = convertPriceUnit(FARM_AGENT_UNIT, "10a", "평");

    setFormData((prev) => ({
      ...prev,
      pnu,
      allArea,
      quantity,
      farmAgentUnitPrice,
    }));
  }, []);

  useEffect(() => {
    const allAreaM2 = toM2(formData.allArea, "평");
    const area = formData.area ? formData.area : formData.allArea;
    const areaM2 = toM2(area, "평");
    const quantityPerM2 = formData.quantity / toM2(1, formData.unit);

    const totalQuantity = quantityPerM2 * allAreaM2;
    const sales = Math.round((totalQuantity * UNIT_PRICE_BASE) / 10000);
    const empLaborCost = Math.ceil(
      areaM2 * (defaultCost.empLaborCostPerUnit / 1000)
    ); // 연 인건비
    const ownLaborCost = Math.ceil(
      allAreaM2 * (defaultCost.ownLaborCostPerUnit / 1000)
    ); // 연 자가노동비
    const materialCost = Math.ceil(
      allAreaM2 * (defaultCost.materialCostPerUnit / 1000)
    ); // 연 재료비
    const waterHeatingCost = Math.ceil(
      allAreaM2 * (defaultCost.waterHeatingCostPerUnit / 1000)
    ); // 연 수도광열비
    const farmEquipmentRentalCost = Math.ceil(
      allAreaM2 * (defaultCost.farmEquipmentRentalCostPerUnit / 1000)
    ); // 연 농구렌탈비
    const sga = Math.ceil(allAreaM2 * (defaultCost.sgaPerUnit / 1000)); // 연 기타 판매관리비

    setFormData((prev) => ({
      ...prev,
      sales,
      empLaborCost,
      initialOwnLabor: ownLaborCost,
      materialCost,
      waterHeatingCost,
      farmEquipmentRentalCost,
      sga,
    }));
  }, [formData.allArea, formData.area]);

  const createReport = async () => {
    const areaPerUnitM2 = toM2(1, formData.unit);
    const quantityPerM2 = formData.quantity / areaPerUnitM2;
    try {
      const data = {
        pnu: formData.pnu,
        area: formData.area
          ? toM2(formData.area, "평")
          : toM2(formData.allArea, "평"),
        farmAgentArea: formData.area ? toM2(formData.farmAgentArea, "평") : 0,
        farmAgentUnitPrice: convertUnitPriceToPerM2(
          formData.farmAgentUnitPrice,
          formData.unit
        ),
        farmAgentRate: formData.farmAgentRate,
        crop: formData.crop,
        // crops: formData.crops,
        quantity: quantityPerM2,
        unitPrice: formData.unitPrice,
        // growthDays: formData.growthDays,
        // rainyDays: formData.rainyDays,
        // lossDays: formData.lossDays,
        // lossMachineDays: formData.lossMachineDays,
        // workingHours: formData.workingHours,
        efd: formData.efd / 100,
        avgWorkingHours: formData.avgWorkingHours,
        financialCost: formData.financialCost,
        // ? formData.financialCost * 10000
        // : formData.financialCost * 12 * 10000,
        empLaborCost: formData.empLaborCost,
        // formData.isCostYear
        // ? (formData.empLaborCost - formData.ownLaborCost) * 10000
        // : (formData.empLaborCost - formData.ownLaborCost) *
        //   formData.growthDays *
        //   10000,
        ownLaborCost: formData.ownLaborCost,
        // formData.isCostYear
        // ? formData.ownLaborCost * 10000
        // : formData.ownLaborCost * formData.growthDays * 10000,
        isOwnLabor: formData.isOwnLabor,
        landCost: formData.isCostYear
          ? formData.landCost * 10000
          : formData.landCost * formData.growthDays * 10000,
        materialCost: formData.materialCost,
        // formData.isCostYear
        // ? formData.materialCost * 10000
        // : formData.materialCost * formData.growthDays * 10000,
        waterHeatingCost: formData.waterHeatingCost,
        // formData.isCostYear
        // ? formData.waterHeatingCost * 10000
        // : formData.waterHeatingCost * formData.growthDays * 10000,
        farmEquipmentRentalCost: formData.farmEquipmentRentalCost,
        // formData.isCostYear
        // ? formData.farmEquipmentRentalCost * 10000
        // : formData.farmEquipmentRentalCost * formData.growthDays * 10000,
        sga: formData.sga,
        // formData.isCostYear
        // ? formData.sga * 10000
        // : formData.sga * formData.growthDays * 10000,
        tractorPurchaseCost: formData.tractorPurchaseCost,
        tractorMaintenanceCost: formData.tractorMaintenanceCost,
        tractorNum: formData.tractorNum,
        implementList: formData.implementList.map((item) => ({
          ...item,
          price: item.price * 10000,
          maintenance: item.maintenance * 10000,
        })),
      };
      if (!isMobile(window.innerWidth)) {
        setClose(false);
      }
      const result = await TractorService.report(data);
      if (result.success) {
        if (isMobile(window.innerWidth)) {
          navigate(`/machine/report/${result.reportIdx}`);
        } else {
          setReportIdx5(result.reportIdx);
          setClose(true);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <div className="flex-column w-100per  bg-white">
      <Header>
        <img
          src="/asset/images/arrow/backArrowTopNav.svg"
          alt=""
          className="mobile-only"
          onClick={handleBack}
        />
        <Title>농기계 효율 분석</Title>
        <img
          src={CloseIcon}
          alt=""
          className="w-24 hover"
          onClick={() => navigate(-1)}
        />
      </Header>

      <StickyTabWrapper ref={tabRef} $top={isFixed ? fixedH : 0}>
        {tabList.map((t) => (
          <Tab $active={activeTab === t} onClick={() => handleClickTab(t)}>
            {t}
          </Tab>
        ))}
      </StickyTabWrapper>

      {isFixed && (
        <div className="flex-column  browser-fixed bg-white browser-w-360 top-0 z-20 border-r-171E2610">
          <div
            className="flex-row space-between  border-b-171E26 browser h-48 padding-12-23"
            ref={fixedRef}
          >
            <img
              src={backArrow}
              alt="back"
              className="hover"
              onClick={() => {
                navigate(-1);
              }}
            />
            <div className="font-medium f-size-16 line-h-24 color-text-primary">
              {location2}
            </div>
            <div>&nbsp;</div>
          </div>
        </div>
      )}

      {activeTab === "영농 정보" && (
        <LandInfo
          ref={landInfoRef}
          handleNext={() => {
            if (formData.allArea < 1000) {
              setShowAlert(true);
              return;
            }
            setActiveTab("트랙터/부속기");
          }}
          formData={formData}
          setFormData={setFormData}
          temp={temp}
        />
      )}
      {activeTab === "트랙터/부속기" && (
        <TractorInfo
          formData={formData}
          setFormData={setFormData}
          handleNext={() => setActiveTab("작업 및 가중치")}
        />
      )}
      {activeTab === "작업 및 가중치" && (
        <TaskInfo
          formData={formData}
          setFormData={setFormData}
          handleSubmit={createReport}
        />
      )}

      {showAlert && (
        <MinAreaAlert
          onClose={() => setShowAlert(false)}
          onConfirm={() => setActiveTab("트랙터/부속기")}
        />
      )}
    </div>
  );
}

export default MachineReport;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  background: #fff;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const Title = styled.div`
  flex: 1 0 0;
  font-weight: 700;
  font-size: 16px;
  color: #171e26;

  @media (max-width: 1023px) {
    font-weight: 500;
    text-align: center;
  }
`;

const StickyTabWrapper = styled.div<{ $top: number }>`
  display: flex;
  position: sticky;
  top: ${({ $top }) => $top}px;
  z-index: 19;
  padding-top: 8px;
  gap: 24px;
  align-items: center;
  background: #fff;
  border-bottom: 1px solid rgba(23, 30, 38, 0.1);

  @media (min-width: 1024px) {
    padding: 14px 16px 0;
  }
`;

const Tab = styled.div<{ $active?: boolean }>`
  cursor: pointer;
  display: flex;
  padding: 8px 0;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  color: rgba(23, 30, 38, 0.4);
  font-size: 15px;
  font-weight: 700;
  line-height: 26px; /* 144.444% */
  letter-spacing: -0.18px;

  ${({ $active }) =>
    $active &&
    `
  border-bottom: 2px solid #171E26;
  color: #171E26;

`}

  @media (min-width: 1024px) {
    font-size: 18px;
    width: fit-content;
    flex: 0 0 auto;
  }
`;
